import { WHATSAPP_URL, IMAGES } from "@/config/site";

const REPOSITORY_LAYERS = [
  {
    title: "The Physical Infrastructure Layer",
    tag: "The Anchor",
    testid: "repo-layer-physical",
    image: {
      src: "/assets/specie-vaulting-branded.jpg",
      alt: "Specie vaulting chamber — Tier-1 capital: audited Au and Ag bullion with real-time volumetric proof-of-reserve scanning above the agroforestry yield factory",
    },
    items: [
      {
        head: "The Subsurface Vault",
        body: "An audited, high-security repository holding un-hypothecated physical Gold (Au) and Silver (Ag) bullion bars. This acts as our private Tier-1 risk capital base, absorbing liquidity shocks and grounding internal ledger valuations.",
      },
      {
        head: "The 1-Acre Permaculture Matrix",
        body: "Located directly above the vault are the 4-foot wide contour swales and 18-inch uncompacted soil berms. This footprint functions as a physical asset factory, passively capturing rainwater to feed high-yield Medjool date palms, peach, pomegranate, mulberry, and cherry trees. The resulting crops are logged instantly as tangible inventory assets.",
      },
    ],
  },
  {
    title: "The Private OTC Transaction Layer",
    tag: "At Arm's Length",
    testid: "repo-layer-otc",
    image: {
      src: "/assets/otc-netting-branded.jpg",
      alt: "OTC transaction and smart contract netting — arm's-length swap desk with atomic settlement and non-delivery lockout",
    },
    items: [
      {
        head: "Good-Faith Settlement Desk",
        body: "A glass over-the-counter (OTC) trading hub designed for sovereign entities to execute direct, private capital allocations. Contracts are formulated at arm's length to ensure complete transparency and legal autonomy.",
      },
      {
        head: "Warehouse Line Advances",
        body: "Private partners submit collateral packages — land deeds, future crop rights, or unrefined metal drafts. In return, the desk originates short-term warehouse credit lines using its own private capital pools, bypassing third-party clearing houses.",
      },
      {
        head: "Asset-Backed Promissory Notes",
        body: "Rather than routing paper through mainstream commercial networks, the desk acts as a market maker — discounting, purchasing, and monetizing private bills of exchange natively at present value using its own repository framework.",
      },
    ],
  },
  {
    title: "The Programmatic & Data Layer",
    tag: "Autonomous Execution",
    testid: "repo-layer-data",
    image: {
      src: "/assets/clearing-engine-branded.jpg",
      alt: "Autonomous repository clearing engine — proof-of-reserve oracle module, double-entry crypto-accounting ledger, and smart ledger clearing and netting finality terminal",
    },
    items: [
      {
        head: "Zero-Counterparty Ledger Terminal",
        body: "Dual-screen monitors run localized cryptographic data pipelines. When an OTC swap occurs, the ownership transfer of physical metals or a future fruit harvest settles instantly on an autonomous database, preventing double-spending without central bank oversight.",
      },
      {
        head: "Retail Bank Program Statements",
        body: "Dedicated programming modules code the algorithmic rulebooks, loan-to-value (LTV) constraints, and risk-weight parameters — enabling selected retail banking partners to recognize the repository's warehouse receipts as eligible collateral for working capital.",
      },
      {
        head: "Global Macro Hedging Engine",
        body: "Terminals map local assets directly to derivative capital markets. The desk structures commodity futures contracts to lock in fixed margins on the food forest's fruit yields while managing options straddles to insulate the network's private debt from global inflationary shocks.",
      },
    ],
  },
];

const NAV_SQUARES = [
  {
    src: "/assets/specie-por-nav.png",
    label: "Specie PoR",
    href: "#repo-layer-physical",
    testid: "nav-square-specie",
  },
  {
    src: "/assets/yield-factory-nav.png",
    label: "Yield Factory",
    href: "#orchard",
    testid: "nav-square-yield",
  },
  {
    src: "/assets/netting-engine-nav.png",
    label: "Netting Engine",
    href: "#repo-layer-data",
    testid: "nav-square-netting",
  },
  {
    src: "/assets/settlement-lock-nav.png",
    label: "Settlement Lock",
    href: "#repo-layer-otc",
    testid: "nav-square-settlement",
  },
];

const Exchange = () => {
  return (
    <section
      id="exchange"
      data-testid="exchange-section"
      className="py-24 px-6 border-b border-stone-800 bg-stone-950"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 space-y-6">
          <div className="mono-tag text-xs tracking-wider uppercase text-amber-500">
            Autonomous Infrastructure
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-100 display-serif">
            Bourg Merchant Exchange
          </h2>
          <p className="text-sm text-stone-300 leading-relaxed">
            The only privatized nonmarketable exchange — a 501(c)(2) title-holding entity of the
            Institute. Operating across global debt capital markets as a FIG for retail
            institutions, establishing proper modes of operation and providing warehouse line
            facilities on private decentralized servers.
          </p>
          <div className="space-y-3 pt-2">
            <div className="flex items-start gap-3 text-xs text-stone-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
              <span>
                <strong>Decentralized Banking System:</strong> Private bank without charters that
                does not hold depositors, hosted on proprietary institute servers.
              </span>
            </div>
            <div className="flex items-start gap-3 text-xs text-stone-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
              <span>
                <strong>Repository Statement Control:</strong> Authoring program statements and
                warehouse lines using autonomous lendable funds.
              </span>
            </div>
          </div>
          <div className="pt-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="exchange-whatsapp-cta"
              className="inline-block px-5 py-2.5 rounded bg-stone-900 hover:bg-stone-800 border border-stone-700 text-stone-200 text-sm font-semibold transition-colors"
            >
              Request Briefing via WhatsApp
            </a>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="p-6 rounded-2xl bg-stone-900/80 border border-stone-800 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-stone-800 text-xs font-mono text-stone-400">
              <span>SERVER NODE // PRIVATIZED</span>
              <span className="text-amber-500">ENCRYPTED REPOSITORY</span>
            </div>
            <div className="aspect-[16/9] rounded-lg border border-stone-800 relative overflow-hidden">
              <img
                src={IMAGES.exchange.url}
                alt={IMAGES.exchange.alt}
                data-testid="exchange-image"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent pointer-events-none"></div>
              <span className="absolute bottom-3 left-0 right-0 text-center text-xs text-stone-300 font-mono">
                Bourg Merchant Exchange // Decentralized Framework
              </span>
            </div>
            <div className="text-[11px] text-stone-400 leading-normal">
              Nonmarketable capital structure serving member enterprises and institutional education
              partners globally.
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid="repository-mechanics"
        className="max-w-7xl mx-auto mt-20 pt-16 border-t border-stone-800/80 space-y-10"
      >
        <div className="max-w-3xl space-y-3">
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            Structural Mechanics
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-100 display-serif">
            The Autonomous BMEX Repository
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed">
            This rendering models the spatial layout and physical flow of capital, showing how
            high-aptitude financial engineering functions through decentralized, direct-contact
            operations.
          </p>
        </div>

        <figure
          data-testid="repository-diagram"
          className="rounded-2xl border border-stone-800 bg-stone-900/80 p-4"
        >
          <div className="rounded-xl border border-stone-800 overflow-hidden">
            <img
              src="/assets/clearing-loop-branded.jpg"
              alt="Private Asset Clearing Loop — Chamber 1 asset capture and proof-of-reserve, Chamber 2 programmatic netting and fractional T-accounts, Chamber 3 autonomous drawdown controller and ledger"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <figcaption className="pt-3 text-center mono-tag text-[10px] uppercase tracking-widest text-stone-500">
            Private Asset Clearing Loop · Three-Chamber Autonomous Settlement
          </figcaption>
        </figure>

        <div className="grid lg:grid-cols-2 gap-6">
          <figure
            data-testid="repository-matrix-figure"
            className="rounded-2xl border border-stone-800 bg-stone-900/80 p-4"
          >
            <div className="rounded-xl border border-stone-800 overflow-hidden">
              <img
                src="/assets/sovereign-market-matrix-branded.jpg"
                alt="Sovereign Market Matrix — sovereign inputs and asset repositories, arm's-length OTC exchange floor, and autonomous clearing and settlement loop"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <figcaption className="pt-3 text-center mono-tag text-[10px] uppercase tracking-widest text-stone-500">
              Sovereign Market Matrix · Autonomous Real Asset &amp; Capital Flows
            </figcaption>
          </figure>
          <figure
            data-testid="repository-core-figure"
            className="rounded-2xl border border-stone-800 bg-stone-900/80 p-4"
          >
            <div className="rounded-xl border border-stone-800 overflow-hidden">
              <img
                src="/assets/repository-core-branded.jpg"
                alt="Sovereign Private Repository — Chamber 1 quant oracle node with asset volumetric metrics, Chamber 2 programmatic LTV computation engine, Chamber 3 autonomous drawdown controller and settlement fault circuit"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <figcaption className="pt-3 text-center mono-tag text-[10px] uppercase tracking-widest text-stone-500">
              BOURG MERCHANT EXCHANGE · Sovereign Private Repository &amp; Autonomous Clearing Core (OTC)
            </figcaption>
          </figure>
        </div>

        <nav
          data-testid="repository-nav-squares"
          aria-label="Repository system navigation"
          className="grid grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {NAV_SQUARES.map((square) => (
            <a
              key={square.testid}
              href={square.href}
              data-testid={square.testid}
              className="group block"
            >
              <span className="repo-icon-tile block rounded-2xl overflow-hidden repo-glow-gold border border-stone-800">
                <img
                  src={square.src}
                  alt={square.label}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </span>
              <span className="block text-center mt-3 mono-tag text-[10px] uppercase tracking-widest text-stone-400 group-hover:text-amber-400 transition-colors">
                {square.label}
              </span>
            </a>
          ))}
        </nav>

        <div className="grid md:grid-cols-3 gap-6">
          {REPOSITORY_LAYERS.map((layer) => (
            <div
              key={layer.testid}
              data-testid={layer.testid}
              className="rounded-xl border border-stone-800 bg-stone-900/60 p-6 space-y-5"
            >
              <div className="rounded-lg border border-stone-800 overflow-hidden">
                <img
                  src={layer.image.src}
                  alt={layer.image.alt}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <div className="space-y-1">
                <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-400/90">
                  {layer.tag}
                </span>
                <h4 className="text-base font-bold text-stone-100">{layer.title}</h4>
              </div>
              <div className="space-y-4">
                {layer.items.map((item) => (
                  <div key={item.head} className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-semibold text-stone-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                      {item.head}
                    </div>
                    <p className="text-[11px] text-stone-400 leading-relaxed pl-3.5">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Exchange;
